import moment from 'moment';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

import {
    Box,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
    Typography,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    FormHelperText,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { SCUIButton, SCUILoadingLinear, SCUIDateRangerV2 } from '../../../../../atomic/atoms';
import { SCUIGeneralDrawer } from '../../../../../atomic/molecules';

import { voucherTypes, statusOptions, IGiftVoucherData } from '../enum';
import { GiftVoucherQuery } from '../../../../../services/query';
import { GV } from '../giftVouchers.classes';

export default function GiftVoucherDrawer({
    clearSelected,
    isDrawerOpen,
    setStartDate,
    startDate,
    setEndDate,
    endDate,
    setIsLoading,
    customerData,
    voucherType,
    getGiftVouchers,
    isLoading,
    selectedGift,
    control,
    handleSubmit,
    setVoucherType,
}: any) {
    const regionId: string = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.regionId?.toString();
    const countryId: any = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId?.toString();

    const languages = JSON.parse(import.meta.env.VITE_LANGUAGE_LIST!).filter((x: any) =>
        x.countries.includes(countryId)
    );
    const { t } = useTranslation();
    const handleDrawerClose = () => {
        clearSelected();
    };

    const _handlChangeDate = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    const onSubmit = (data: any) => {
        postGiftVoucher(data);
    };

    const postGiftVoucher = (formData: any) => {
        if (!formData) return;
        setIsLoading(true);

        const languageValues = (languages || []).map((languageItem: any) => {
            const key = `language-${languageItem.id}`;
            return {
                languageId: languageItem.id,
                keywordValue: formData[key] || null,
            };
        });

        const postPayload = {
            giftVoucher: {
                id: formData.id || 0,
                customerId: customerData?.customer?.customerId,
                status: formData.status,
                operatorId: JSON.parse(localStorage.getItem('checkOperator') || '{}').operatorId,
                couponType: voucherType,
                value: formData.value || 0,
                minOrderGrandTotalAmount: formData.minAmount || 0,
                maxDiscountAmount:
                    formData.maxAmount || voucherTypes.find((item: any) => item.id === voucherType)?.key === 'amount'
                        ? 999999
                        : 0,
                description: formData.description,
                couponSource: 1, // source which the coupon is generated for ex: TTNET
                startDate: moment.utc(startDate, 'DD-MM-YYYY').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                endDate: moment.utc(endDate, 'DD-MM-YYYY').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                regionId,
            },
            titleTexts: languageValues,
        };

        GiftVoucherQuery.PostGiftVoucher(postPayload)
            .then(() => {
                getGiftVouchers();
            })
            .catch(() => {
                setIsLoading(false);
                Swal.fire(t('general.error'), '', 'error');
            });
    };

    return (
        <SCUIGeneralDrawer
            open={isDrawerOpen}
            onClose={(_: any, reason: any) => {
                if (reason === 'backdropClick') {
                    handleDrawerClose();
                }
            }}
            width={'400px'}
        >
            {isLoading && <SCUILoadingLinear display={isLoading} />}
            <Box className={GV.drawerBody} sx={{ padding: '8px' }}>
                <Typography
                    className={GV.drawerTitle}
                    sx={{ margin: '12px 0 12px 16px', fontWeight: 'bold', fontSize: '18px !important' }}
                >
                    {selectedGift ? t('giftVoucher.editVoucher') : t('giftVoucher.addVoucher')}
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box
                        className={GV.drawerForm}
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        p={2}
                        maxWidth={500}
                    >
                        <Box className={GV.fieldDateRange}>
                            <SCUIDateRangerV2
                                fromDate={startDate}
                                toDate={endDate}
                                setDateRange={_handlChangeDate}
                                dateFormat="DD/MM/YYYY"
                                label={t('giftVoucher.startDate') + ' - ' + t('giftVoucher.endDate')}
                                fullWidth
                            />
                        </Box>
                        <Controller
                            name="status"
                            control={control}
                            rules={{ required: t('giftVoucher.validationMessage.required') }}
                            render={({ field, fieldState }) => (
                                <FormControl className={GV.fieldStatus} fullWidth error={!!fieldState.error}>
                                    <InputLabel>{t('giftVoucher.status')}</InputLabel>
                                    <Select
                                        {...field}
                                        className={GV.inputSelectStatus}
                                        error={!!fieldState.error}
                                        value={field.value || ''}
                                    >
                                        {statusOptions.map((statusItem: any) => (
                                            <MenuItem key={statusItem.id} value={statusItem.id}>
                                                {t(statusItem.name)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                        {(languages || []).length > 0 &&
                            languages.map((language: any, index: number) => {
                                let name = `language-${language.id}` as keyof IGiftVoucherData;

                                return (
                                    <div key={index} className={GV.fieldLanguageRow}>
                                        <Controller
                                            name={name}
                                            control={control}
                                            rules={{
                                                required: t('giftVoucher.validationMessage.required'),
                                                minLength: {
                                                    value: 2,
                                                    message: t('giftVoucher.validationMessage.languageMin', {
                                                        count: 2,
                                                    }),
                                                },
                                                maxLength: {
                                                    value: 50,
                                                    message: t('giftVoucher.validationMessage.languageMax', {
                                                        count: 50,
                                                    }),
                                                },
                                            }}
                                            render={({ field, fieldState }) => (
                                                <FormControl fullWidth error={!!fieldState.error}>
                                                    <TextField
                                                        {...field}
                                                        className={GV.inputTextfieldLanguage}
                                                        value={field.value || ''}
                                                        error={!!fieldState.error}
                                                        fullWidth
                                                        label={t(language.name || '')}
                                                    />
                                                    {fieldState.error && (
                                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </div>
                                );
                            })}
                        <Controller
                            name="description"
                            control={control}
                            rules={{ required: t('giftVoucher.validationMessage.required') }}
                            render={({ field, fieldState }) => (
                                <FormControl className={GV.fieldDescription} fullWidth error={!!fieldState.error}>
                                    <TextField
                                        {...field}
                                        className={GV.inputTextfieldDescription}
                                        value={field.value || ''}
                                        error={!!fieldState.error}
                                        label={t('general.description')}
                                        multiline
                                        rows={2}
                                    />
                                    {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                        <Box
                            className={GV.fieldSaleType}
                            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                        >
                            <Typography sx={{ marginRight: '16px', fontWeight: '500' }}>
                                {t('giftVoucher.saleType')}
                                {':'}
                            </Typography>
                            <RadioGroup
                                className={GV.inputRadioGroupSaleType}
                                value={voucherType}
                                onChange={(e) => setVoucherType(+e.target.value)}
                                row
                            >
                                <FormControlLabel
                                    value={voucherTypes[0].id}
                                    control={<Radio />}
                                    label={t('giftVoucher.amount')}
                                />
                                <FormControlLabel
                                    value={voucherTypes[1].id}
                                    control={<Radio />}
                                    label={t('giftVoucher.percentage')}
                                />
                            </RadioGroup>
                        </Box>
                        <Controller
                            name="value"
                            control={control}
                            rules={{
                                required: t('giftVoucher.validationMessage.required'),
                                validate: {
                                    notZero: (value) => value > 0 || t('giftVoucher.validationMessage.notZero'),
                                    maxValue: (value) => {
                                        const isPercentage =
                                            voucherTypes.find((item: any) => item.id === voucherType)?.key ===
                                            'percentage';
                                        return (
                                            !isPercentage ||
                                            value <= 25 ||
                                            t('giftVoucher.validationMessage.maxValue', { max: 25 })
                                        );
                                    },
                                },
                            }}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth error={!!fieldState.error}>
                                    <TextField
                                        {...field}
                                        className={GV.inputTextfieldValue}
                                        value={field.value || ''}
                                        error={!!fieldState.error}
                                        label={
                                            voucherTypes.find((item: any) => item.id === voucherType)?.key ===
                                            'percentage'
                                                ? t('giftVoucher.percentage')
                                                : t('giftVoucher.amount')
                                        }
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        type="number"
                                    />
                                    {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                        {voucherTypes.find((item: any) => item.id === voucherType)?.key === 'percentage' && (
                            <>
                                <Controller
                                    name="maxAmount"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            className={GV.inputTextfieldMaxAmount}
                                            value={field.value || ''}
                                            label={t('giftVoucher.maxSaleAmount')}
                                            type="number"
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                    )}
                                />
                            </>
                        )}
                        <Controller
                            name="minAmount"
                            control={control}
                            rules={{ required: t('giftVoucher.validationMessage.required') }}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth error={!!fieldState.error}>
                                    <TextField
                                        {...field}
                                        className={GV.inputTextfieldMinAmount}
                                        value={field.value || ''}
                                        error={!!fieldState.error}
                                        label={t('giftVoucher.minShoppingAmount')}
                                        type="number"
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                    {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                        <Box sx={{ marginTop: '8px' }}>
                            <SCUIButton
                                className={GV.inputButtonSave}
                                text={t('general.save')}
                                type="primary"
                                sizeType="default"
                                buttonType="submit"
                                size={'s'}
                            />
                        </Box>
                    </Box>
                </form>
            </Box>
        </SCUIGeneralDrawer>
    );
}
